import { FC, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { createIdentificationStatusQueryOptions } from "@/hooks/identificationStatusQueryOptions";
import { KYC_STATUS_ONBOARDING_NOT_FINISHED } from "@/hooks/useNeedsIDCheck";
import {
  useGetIdentificationStatus,
  useGetIdentificationURL,
} from "@/services/api";
import { IdentificationStatusResponseKycStatus } from "@/services/model";

import { WarningWithKYCButton } from "./WarningWithKYCButton";

const OPEN_COPY = {
  default: {
    title: "cockpit.idCheck.title",
    info: "cockpit.idCheck.info",
  },
  fromOrder: {
    title: "cockpit.idCheck.titleFromOrder",
    info: "cockpit.idCheck.infoFromOrder",
  },
} as const;

const RETRY_COPY = {
  [IdentificationStatusResponseKycStatus.KYC_error_processing_selfie]: {
    title: "cockpit.kycSection.title.KYC_error_processing_selfie",
    info: "cockpit.kycSection.errors.KYC_error_processing_selfie",
  },
  [IdentificationStatusResponseKycStatus.KYC_error_processing_docs]: {
    title: "cockpit.kycSection.title.KYC_error_processing_docs",
    info: "cockpit.kycSection.errors.KYC_error_processing_docs",
  },
  [IdentificationStatusResponseKycStatus.KYC_invalid_document]: {
    title: "cockpit.kycSection.title.invalidDocument",
    info: "cockpit.kycSection.errors.invalidDocument",
  },
  [IdentificationStatusResponseKycStatus.KYC_ID_uncomparable]: {
    title: "cockpit.kycSection.title.IDUncomparable",
    info: "cockpit.kycSection.errors.IDUncomparable",
  },
} as const;

type RetryStatus = keyof typeof RETRY_COPY;

const isRetryStatus = (
  kycStatus: string | undefined,
): kycStatus is RetryStatus =>
  kycStatus !== undefined && Object.keys(RETRY_COPY).includes(kycStatus);

type KYCWarningProps = {
  /** Post-checkout entry: use order-context intro copy for OPEN status. */
  fromOrder?: boolean;
};

export const KYCWarning: FC<KYCWarningProps> = ({ fromOrder = false }) => {
  const { t, i18n } = useTranslation();

  const staleTime = useRef(0);
  const { data: identificationStatus } = useGetIdentificationStatus({
    query: createIdentificationStatusQueryOptions(staleTime),
  });
  const kycStatus = identificationStatus?.kycStatus;

  const { data: identificationUrl } = useGetIdentificationURL({
    query: {
      enabled:
        kycStatus === IdentificationStatusResponseKycStatus.OPEN ||
        isRetryStatus(kycStatus),
      refetchOnWindowFocus: true,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  });

  const identificationUrlWithLanguage = useMemo(() => {
    if (identificationUrl) {
      const url = new URL(identificationUrl);
      url.searchParams.set("lang", i18n?.resolvedLanguage ?? "en");

      return url.toString();
    }
  }, [identificationUrl, i18n.resolvedLanguage]);

  if (!identificationStatus) {
    return null;
  }

  // Onboarding incomplete — KYC is not actionable yet; hide the todo entirely.
  if (
    (kycStatus as string | undefined) === KYC_STATUS_ONBOARDING_NOT_FINISHED
  ) {
    return null;
  }

  if (isRetryStatus(kycStatus)) {
    const copy = RETRY_COPY[kycStatus];

    return (
      <WarningWithKYCButton
        title={t(copy.title)}
        info={t(copy.info)}
        buttonLabel={t("cockpit.idCheck.button")}
        target={identificationUrlWithLanguage}
      />
    );
  }

  const openCopy = OPEN_COPY[fromOrder ? "fromOrder" : "default"];

  return (
    <WarningWithKYCButton
      title={t(openCopy.title)}
      info={t(openCopy.info)}
      warningInfo={t("cockpit.idCheck.internationalCallsWarning")}
      buttonLabel={t("cockpit.idCheck.button")}
      target={identificationUrlWithLanguage}
    />
  );
};
